import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { useLanguage } from '@context'
import { showApiErrorToast, showSuccessToast } from '@utils'
import { EVENTS_QUERY_KEY } from '../../queries/events/useEventQueries'
import { ADMIN_QUERY_KEY } from '../../queries/admin/useAdminQueries'
import type {
  AdminOrganizer,
  AdminSettings,
  FeePaidBy,
  OrganizerGrantInput,
  OrganizerInvitation,
  PlatformBilling,
  OrganizerBilling,
  OrganizerRequest,
} from '../../queries/admin/types'

const useInvalidateAdmin = () => {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [ADMIN_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] }),
    ])
  }
}

export interface OrganizerRequestInput {
  organizationName: string
  message: string
  websiteUrl?: string
}

export const useSubmitOrganizerRequest = () => {
  const invalidate = useInvalidateAdmin()
  const { t } = useLanguage()
  return useMutation<OrganizerRequest, Error, OrganizerRequestInput>({
    mutationFn: async (body) => (await api.post<OrganizerRequest>('/me/organizer-request', body)).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('admin.request.toast.sentTitle'), t('admin.request.toast.sentMessage'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

export const useApproveOrganizerRequest = () => {
  const invalidate = useInvalidateAdmin()
  const { t } = useLanguage()
  return useMutation<OrganizerRequest, Error, string>({
    mutationFn: async (id) =>
      (await api.post<OrganizerRequest>(`/admin/organizer-requests/${id}/approve`)).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('admin.requests.toast.approved'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

export const useRejectOrganizerRequest = () => {
  const invalidate = useInvalidateAdmin()
  const { t } = useLanguage()
  return useMutation<OrganizerRequest, Error, { id: string; reason: string }>({
    mutationFn: async ({ id, reason }) =>
      (await api.post<OrganizerRequest>(`/admin/organizer-requests/${id}/reject`, { reason })).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('admin.requests.toast.rejected'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

export const useSaveOrganizerBilling = (accountId: string) => {
  const invalidate = useInvalidateAdmin()
  const { t } = useLanguage()
  return useMutation<OrganizerBilling, Error, { commissionRateBps: number; feePaidBy: FeePaidBy }>({
    mutationFn: async (body) =>
      (await api.put<OrganizerBilling>(`/admin/organizers/${accountId}/billing`, body)).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('admin.organizer.toast.saved'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

export const useRemoveOrganizerRole = (accountId: string) => {
  const invalidate = useInvalidateAdmin()
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  return useMutation<unknown, Error, void>({
    mutationFn: async () =>
      (await api.put<unknown>(`/admin/users/${accountId}/organizer`, { isOrganizer: false })).data,
    onSuccess: async () => {
      // The account is no longer an organizer: its detail and billing would 404.
      queryClient.removeQueries({ queryKey: [ADMIN_QUERY_KEY, 'detail', accountId] })
      queryClient.removeQueries({ queryKey: [ADMIN_QUERY_KEY, 'billing', accountId] })
      await invalidate()
      showSuccessToast(t('admin.organizer.toast.removed'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

/** Changes the organization name shown for an organizer. */
export const useRenameOrganization = (accountId: string) => {
  const invalidate = useInvalidateAdmin()
  const { t } = useLanguage()
  return useMutation<AdminOrganizer, Error, string>({
    mutationFn: async (organizationName) =>
      (await api.patch<AdminOrganizer>(`/admin/organizers/${accountId}`, { organizationName })).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('admin.organizer.toast.renamed'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

/** Gives the organizer role back to an account whose role was removed. */
export const useReactivateOrganizerRole = (accountId: string) => {
  const invalidate = useInvalidateAdmin()
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  return useMutation<unknown, Error, void>({
    mutationFn: async () =>
      (await api.put<unknown>(`/admin/users/${accountId}/organizer`, { isOrganizer: true })).data,
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: [ADMIN_QUERY_KEY, 'billing', accountId] })
      await invalidate()
      showSuccessToast(t('admin.organizer.toast.reactivated'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

/** Platform-wide default commission (applies to future payments only). */
export const useSaveAdminSettings = () => {
  const invalidate = useInvalidateAdmin()
  const { t } = useLanguage()
  return useMutation<AdminSettings, Error, PlatformBilling>({
    mutationFn: async (body) => (await api.put<AdminSettings>('/admin/settings', body)).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('admin.settings.toast.saved'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

/** Gives the organizer role to an existing account (optional organization + commission). */
export const useGiveOrganizerRole = () => {
  const invalidate = useInvalidateAdmin()
  const { t } = useLanguage()
  return useMutation<AdminOrganizer, Error, OrganizerGrantInput & { accountId: string }>({
    mutationFn: async (body) => (await api.post<AdminOrganizer>('/admin/organizers', body)).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('admin.add.toast.given'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

/** Invites an email with no account yet: it becomes an organizer when it signs up. */
export const useInviteOrganizer = () => {
  const invalidate = useInvalidateAdmin()
  const { t } = useLanguage()
  return useMutation<OrganizerInvitation, Error, OrganizerGrantInput & { email: string }>({
    mutationFn: async (body) =>
      (await api.post<OrganizerInvitation>('/admin/organizer-invitations', body)).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('admin.add.toast.invited'))
    },
    // The 409 "account exists" is handled by the screen (it offers to give the role instead).
  })
}

export const useCancelOrganizerInvitation = () => {
  const invalidate = useInvalidateAdmin()
  const { t } = useLanguage()
  return useMutation<unknown, Error, string>({
    mutationFn: async (id) => (await api.delete(`/admin/organizer-invitations/${id}`)).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('admin.add.toast.cancelled'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}
